import { useState } from "react";
import { MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import {
  MDSSidebarSection,
  MDSSidebarSectionHeader,
  MDSSidebarSectionHeaderSlotName,
  MDSSidebarSectionItem,
  MDSSidebarSectionItemSlotName,
  MDSSidebarSectionSlotName,
  MDSSidebarSectionEmpty,
} from "@/mds/components/sidebar";
import { observer } from "mobx-react-lite";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { SectionListTitleWrapper, SeeAllButton } from "./sidebar-contents.style";
import {
  MDSButtonGroup,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonIcon,
  MDSButtonVariant,
} from "@/mds";
import { actions } from "@/actions";
import { EventContext } from "@/domains/metrics/context";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { motion } from "motion/react";
import styled from "@emotion/styled";

interface SidebarContentsRecentCollectionsListProps {
  onClick?: () => void;
  includeTitle?: boolean;
  indent?: boolean;
}

interface SidebarContentsRecentCollectionsProps {
  onClick?: () => void;
}

export const SidebarContentsRecentCollections = observer(function SidebarContentsRecentCollections({
  onClick,
}: SidebarContentsRecentCollectionsProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const hasRecentCollections =
    store.recentItems.sortedRecentItems.filter(item => item instanceof CollectionObservable)
      .length > 0;

  const handleCreateCollection = () => {
    actions.createCollection({
      title: "",
      store,
      eventContext: EventContext.GlobalActions,
      navigateToCollection: true,
      showToast: false,
    });
  };

  return (
    <MDSSidebarSection isExpanded={isExpanded}>
      <MDSSidebarSectionHeader
        slot={MDSSidebarSectionSlotName.Header}
        label="Collections"
        defaultIconKind={MdsIconKind.Collection}
        isExpanded={isExpanded}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <MDSButtonGroup slot={MDSSidebarSectionHeaderSlotName.ButtonsRight}>
          <MDSTooltip config={{ label: "Create collection", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              size={MDSIconButtonSize.XSmall}
              iconKind={MdsIconKind.Plus}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={handleCreateCollection}
              rounded
            />
          </MDSTooltip>
        </MDSButtonGroup>
      </MDSSidebarSectionHeader>
      {hasRecentCollections && (
        <>
          <SidebarContentsRecentCollectionsList onClick={onClick} indent />
          <SeeAllButton
            onClick={() => {
              onClick?.();
              store.navigation.goToCollections();
            }}
          >
            See All
          </SeeAllButton>
        </>
      )}
      {!hasRecentCollections && (
        <MDSSidebarSectionEmpty>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
            Use collections to organize your notes in Mem
          </MDSText>
          <MDSButton variant={MDSButtonVariant.Subtle} onClick={handleCreateCollection}>
            <MDSButtonIcon kind={MdsIconKind.Plus} />
            Create collection
          </MDSButton>
        </MDSSidebarSectionEmpty>
      )}
    </MDSSidebarSection>
  );
});

export const SidebarContentsRecentCollectionsList = observer(
  function SidebarContentsRecentCollectionsList({
    onClick,
    includeTitle,
    indent,
  }: SidebarContentsRecentCollectionsListProps) {
    const { store } = useAppStore();
    const recentCollections = store.recentItems.sortedRecentItems
      .filter(item => item instanceof CollectionObservable)
      .slice(0, 5);

    return (
      <>
        {includeTitle && (
          <SectionListTitleWrapper>
            <MDSText
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.SemiBold}
              color={MDSTextColor.Secondary}
            >
              Recent Collections
            </MDSText>
          </SectionListTitleWrapper>
        )}
        {recentCollections.map(collection => (
          <motion.div key={collection.id} layoutId={collection.id}>
            <MDSSidebarSectionItem
              key={collection.id}
              label={collection.label}
              iconKind={
                <StyledCollectionIcon size={16} collectionId={collection.id} legacy={false} />
              }
              onClick={() => {
                onClick?.();
                store.navigation.goToCollection({ collectionId: collection.id });
              }}
              indent={indent}
            >
              <MDSButtonGroup slot={MDSSidebarSectionItemSlotName.ButtonsRight}>
                <MDSTooltip
                  config={{
                    label: collection.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSIconButton
                    size={MDSIconButtonSize.XSmall}
                    iconKind={
                      collection.isFavorited ? MdsIconKind.ThumbtackSolid : MdsIconKind.Thumbtack
                    }
                    variant={MDSIconButtonVariant.OnChrome}
                    onClick={() => {
                      onClick?.();
                      collection.toggleFavorite();
                    }}
                    rounded
                  />
                </MDSTooltip>
              </MDSButtonGroup>
            </MDSSidebarSectionItem>
          </motion.div>
        ))}
      </>
    );
  }
);

const StyledCollectionIcon = styled(CollectionIcon)`
  .mds-icon {
    font-size: 11px;
    height: 11px;
    width: 11px;
  }
`;
